/**
 * Membership API Service
 * Handles member details, benefits, and deposit management
 */

// Goes through the server-side proxy (src/app/api/proxy-member/[...path]/route.js)
// which injects the Basic credential — never sent from the client.
const MEMBERSHIP_API_BASE = '/api/proxy-member';

/**
 * Check member details by email
 * @param {string} email - Member email
 * @returns {Promise<Object>} Member details response
 */
export const checkMemberDetails = async (email) => {
  try {
    const response = await fetch(`${MEMBERSHIP_API_BASE}/members/details?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, message: 'Member not found', data: null };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Ensure data.data is always an array or null
    let memberData = data.data;
    if (memberData && !Array.isArray(memberData)) {
      console.warn('API returned non-array data, converting to array:', memberData);
      memberData = [memberData]; // Convert to array if it's an object
    }

    return { success: true, data: memberData || [] };
  } catch (error) {
    console.error('Error checking member details:', error);
    return { success: false, message: error.message, data: null };
  }
};

/**
 * Get member details for specific property
 * @param {string} email - Member email
 * @param {string} propertyCode - Property code (e.g., 'DEMO', 'BII', 'PMH')
 * @returns {Promise<Object>} Member details for the property or null
 */
export const getMemberForProperty = async (email, propertyCode) => {
  try {
    const memberResponse = await checkMemberDetails(email);
    
    if (!memberResponse.success || !memberResponse.data) {
      return null;
    }

    // Find member data for the specific property using hotel_id field
    const memberData = memberResponse.data.find(member =>
      member.hotel_id === propertyCode
    );

    if (memberData) {
      console.log(`Found membership for property ${propertyCode}:`, {
        level: memberData.current_level,
        depositBalance: memberData.deposit_balance,
        benefitsCount: memberData.benefits?.length || 0
      });
    }

    return memberData || null;
  } catch (error) {
    console.error('Error getting member for property:', error);
    return null;
  }
};

/**
 * Check if user has membership for any property
 * @param {string} email - Member email
 * @returns {Promise<Array>} Array of property codes where user has membership
 */
export const getUserMembershipProperties = async (email) => {
  try {
    if (!email) return [];
    
    const memberResponse = await checkMemberDetails(email);
    
    if (!memberResponse.success || !memberResponse.data) {
      return [];
    }

    // Ensure memberResponse.data is an array before calling map
    if (!Array.isArray(memberResponse.data)) {
      console.warn('memberResponse.data is not an array:', typeof memberResponse.data, memberResponse.data);
      return [];
    }

    // Extract all hotel_ids (property codes) where user has membership
    const propertyIds = memberResponse.data.map(member => member.hotel_id);
    console.log(`User ${email} has membership for properties:`, propertyIds);
    
    return propertyIds;
  } catch (error) {
    console.error('Error getting user membership properties:', error);
    return [];
  }
};

/**
 * Check if property has membership program
 * @param {string} email - Member email (optional, can be null for general check)
 * @param {string} propertyCode - Property code
 * @returns {Promise<boolean>} True if property has membership program
 */
export const hasPropertyMembership = async (email, propertyCode) => {
  try {
    // If no email provided, we can't check specific membership
    if (!email) {
      return false;
    }

    const memberData = await getMemberForProperty(email, propertyCode);
    return memberData !== null;
  } catch (error) {
    console.error('Error checking property membership:', error);
    return false;
  }
};

/**
 * Calculate member discount for a rate
 * @param {Array} benefits - Member benefits array
 * @param {number} basePrice - Base room price
 * @returns {Object} Discount information
 */
export const calculateMemberDiscount = (benefits, basePrice) => {
  if (!benefits || benefits.length === 0) {
    return { discount: 0, discountedPrice: basePrice, appliedBenefit: null };
  }

  let bestDiscount = 0;
  let bestDiscountedPrice = basePrice;
  let appliedBenefit = null;

  benefits.forEach(benefit => {
    // Skip benefits without discount or with null discount type
    if (!benefit.discount || !benefit.discount.type || benefit.discount.type === null) {
      return;
    }

    let discount = 0;
    let discountedPrice = basePrice;

    if (benefit.discount.type === 'pct') {
      // Percentage discount - value is the percentage (e.g., "5" for 5%)
      const percentage = parseFloat(benefit.discount.value) || 0;
      discount = Math.round((basePrice * percentage) / 100);
      discountedPrice = basePrice - discount;
      console.log(`Calculating percentage discount: ${percentage}% of ${basePrice} = ${discount}`);
    } else if (benefit.discount.type === 'amount') {
      // Fixed amount discount - value is the fixed amount to subtract
      discount = parseFloat(benefit.discount.value) || 0;
      discountedPrice = Math.max(0, basePrice - discount);
      console.log(`Calculating amount discount: ${discount} off ${basePrice} = ${discountedPrice}`);
    }

    // Keep the best (highest) discount
    if (discount > bestDiscount) {
      bestDiscount = discount;
      bestDiscountedPrice = discountedPrice;
      appliedBenefit = benefit;
    }
  });

  console.log(`Best discount applied: ${bestDiscount} (${appliedBenefit?.name})`);

  return {
    discount: bestDiscount,
    discountedPrice: bestDiscountedPrice,
    appliedBenefit: appliedBenefit
  };
};

/**
 * Process member data from API response and extract benefits
 * @param {Object} memberData - Member data from API
 * @param {string} propertyCode - Property code to match
 * @returns {Object} Processed member information
 */
export const processMemberData = (memberData, propertyCode) => {
  if (!memberData || !Array.isArray(memberData)) {
    return null;
  }

  // Find member data for the specific property
  const propertyMember = memberData.find(member => member.hotel_id === propertyCode);
  
  if (!propertyMember) {
    console.log(`No membership found for property: ${propertyCode}`);
    return null;
  }

  // Extract discount benefits (benefits with discount.type and isDirect: true)
  const discountBenefits = (propertyMember.benefits || []).filter(benefit => 
    benefit.discount && 
    benefit.discount.type && 
    benefit.discount.type !== null &&
    benefit.discount.isDirect === true
  );

  // Extract non-discount benefits (for display)
  const nonDiscountBenefits = (propertyMember.benefits || []).filter(benefit => 
    !benefit.discount || 
    !benefit.discount.type || 
    benefit.discount.type === null ||
    benefit.discount.isDirect === false
  );

  // Extract extra benefit notes (type: null) - for reservation notes only, not display
  const extraBenefitNotes = (propertyMember.benefits || []).filter(benefit => 
    benefit.discount && 
    benefit.discount.type === null
  ).map(benefit => benefit.name || benefit.description);

  return {
    memberInfo: {
      member_id: propertyMember.member_id,
      hotel_id: propertyMember.hotel_id,
      email: propertyMember.email,
      phone: propertyMember.phone,
      hotel_name: propertyMember.hotel_name,
      current_level: propertyMember.current_level,
      level_type: propertyMember.level_type,
      total_spent: propertyMember.total_spent,
      total_stays: propertyMember.total_stays,
      deposit_balance: propertyMember.deposit_balance,
      color_card: propertyMember.color_card,
      join_date: propertyMember.join_date
    },
    discountBenefits,
    nonDiscountBenefits,
    extraBenefitNotes,
    nextLevel: propertyMember.next_level,
    transactions: propertyMember.transactions || []
  };
};

/**
 * Calculate the best discount from member benefits
 * @param {Array} memberData - Array of member data from API
 * @param {string} propertyCode - Property code
 * @param {number} roomPrice - Room price to calculate discount for
 * @returns {Object} Best discount information
 */
export const calculateBestMemberDiscount = (memberData, propertyCode, roomPrice) => {
  const processedData = processMemberData(memberData, propertyCode);
  
  if (!processedData || !processedData.discountBenefits.length) {
    return {
      discount: 0,
      discountedPrice: roomPrice,
      appliedBenefit: null,
      memberInfo: processedData?.memberInfo || null
    };
  }

  const discountInfo = calculateMemberDiscount(processedData.discountBenefits, roomPrice);
  
  return {
    ...discountInfo,
    memberInfo: processedData.memberInfo,
    allBenefits: {
      discount: processedData.discountBenefits,
      nonDiscount: processedData.nonDiscountBenefits
    },
    extraBenefitNotes: processedData.extraBenefitNotes
  };
};

/**
 * Check if member has sufficient deposit balance
 * @param {Object} memberData - Member data object
 * @param {number} amount - Amount to check
 * @returns {boolean} True if sufficient balance
 */
export const hasSufficientDeposit = (memberData, amount) => {
  if (!memberData || !memberData.deposit_balance) return false;
  
  const depositBalance = parseFloat(memberData.deposit_balance) || 0;
  return depositBalance >= amount;
};

/**
 * Format member level with color
 * @param {Object} memberData - Member data object
 * @returns {Object} Formatted level information
 */
export const formatMemberLevel = (memberData) => {
  if (!memberData) return null;

  return {
    level: memberData.current_level || 'Member',
    color: memberData.color_card || '#E5E4E2',
    type: memberData.level_type || '',
    totalSpent: parseFloat(memberData.total_spent) || 0,
    totalStays: parseInt(memberData.total_stays) || 0,
    depositBalance: parseFloat(memberData.deposit_balance) || 0
  };
};

/**
 * Get non-discount benefits (like free services)
 * @param {Array} benefits - Member benefits array
 * @returns {Array} Non-discount benefits
 */
export const getNonDiscountBenefits = (benefits) => {
  if (!benefits || benefits.length === 0) return [];

  return benefits.filter(benefit => 
    !benefit.discount || 
    !benefit.discount.type || 
    benefit.discount.type === null
  );
};

/**
 * Save member session data to localStorage
 * @param {string} email - Member email
 * @param {Object} memberData - Member data for current property
 */
export const saveMemberSession = (email, memberData) => {
  try {
    const sessionData = {
      email,
      memberData,
      propertyCode: memberData?.hotel_id,
      timestamp: Date.now()
    };
    localStorage.setItem(`memberSession_${memberData?.hotel_id || 'default'}`, JSON.stringify(sessionData));
    console.log(`Saved member session for property ${memberData?.hotel_id}:`, {
      level: memberData?.current_level,
      deposit: memberData?.deposit_balance
    });
  } catch (error) {
    console.warn('Error saving member session:', error);
  }
};

/**
 * Get member session data from localStorage for specific property
 * @param {string} propertyCode - Property code to get session for
 * @returns {Object|null} Member session data or null
 */
export const getMemberSession = (propertyCode = 'default') => {
  try {
    const sessionData = localStorage.getItem(`memberSession_${propertyCode}`);
    if (!sessionData) return null;

    const parsed = JSON.parse(sessionData);
    
    // Check if session is less than 1 hour old
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - parsed.timestamp > oneHour) {
      localStorage.removeItem(`memberSession_${propertyCode}`);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Error getting member session:', error);
    localStorage.removeItem(`memberSession_${propertyCode}`);
    return null;
  }
};

/**
 * Clear member session for specific property or all
 * @param {string} propertyCode - Property code to clear, or null for all
 */
export const clearMemberSession = (propertyCode = null) => {
  try {
    if (propertyCode) {
      localStorage.removeItem(`memberSession_${propertyCode}`);
    } else {
      // Clear all member sessions
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('memberSession_')) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    console.warn('Error clearing member session:', error);
  }
};
